import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
  useMemo,
  memo,
  type CSSProperties,
} from 'react';
import { useTheme } from '@/contexts/ThemeContext';

export interface SignatureCanvasProps {
  width?: number;
  height?: number;
  penColor?: string;
  penWidth?: number;
  backgroundColor?: string;
  disabled?: boolean;
  onChange?: (dataUrl: string | null) => void;
  onClear?: () => void;
}

export interface SignatureCanvasHandle {
  clear(): void;
  isEmpty(): boolean;
  toDataURL(type?: string): string;
  fromDataURL(dataUrl: string): void;
}

export const SignatureCanvas = memo(forwardRef<SignatureCanvasHandle, SignatureCanvasProps>(
  function SignatureCanvas(
    {
      width,
      height = 150,
      penColor = '#000000',
      penWidth = 2,
      backgroundColor = '#ffffff',
      disabled = false,
      onChange,
      onClear,
    },
    ref
  ) {
    const { theme } = useTheme();
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const ctxRef = useRef<CanvasRenderingContext2D | null>(null);
    const isDrawingRef = useRef(false);
    const lastXRef = useRef(0);
    const lastYRef = useRef(0);
    const [hasSignature, setHasSignature] = useState(false);

    // Keep latest callback refs to avoid stale closures in event listeners
    const onChangeRef = useRef(onChange);
    onChangeRef.current = onChange;
    const onClearRef = useRef(onClear);
    onClearRef.current = onClear;

    const canvasStyle = useMemo<CSSProperties>(
      () => ({
        touchAction: 'none',
        cursor: disabled ? 'not-allowed' : 'crosshair',
      }),
      [disabled]
    );

    const toDataURL = useCallback((type: string = 'image/png'): string => {
      const canvas = canvasRef.current;
      if (!canvas) return '';
      return canvas.toDataURL(type);
    }, []);

    const clear = useCallback(() => {
      const canvas = canvasRef.current;
      const ctx = ctxRef.current;
      if (!ctx || !canvas) return;

      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      setHasSignature(false);
      onChangeRef.current?.(null);
      onClearRef.current?.();
    }, [backgroundColor]);

    const isEmpty = useCallback((): boolean => {
      return !hasSignature;
    }, [hasSignature]);

    const fromDataURL = useCallback(
      (dataUrl: string) => {
        const canvas = canvasRef.current;
        const ctx = ctxRef.current;
        if (!ctx || !canvas || !dataUrl) return;

        const img = new Image();
        img.onload = () => {
          ctx.fillStyle = backgroundColor;
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(img, 0, 0);
          setHasSignature(true);
        };
        img.src = dataUrl;
      },
      [backgroundColor]
    );

    useImperativeHandle(
      ref,
      () => ({
        clear,
        isEmpty,
        toDataURL,
        fromDataURL,
      }),
      [clear, isEmpty, toDataURL, fromDataURL]
    );

    // Use a ref for hasSignature so event handlers always see the latest value
    // without needing to re-register listeners on every signature state change.
    const hasSignatureRef = useRef(hasSignature);
    hasSignatureRef.current = hasSignature;

    const resizeCanvas = useCallback(() => {
      const canvas = canvasRef.current;
      const ctx = ctxRef.current;
      if (!canvas || !ctx) return;

      const container = canvas.parentElement;
      const newWidth = width || (container ? container.offsetWidth : 400);
      const newHeight = height;

      // Save current drawing
      const imageData = hasSignatureRef.current ? canvas.toDataURL() : null;

      // Resize
      canvas.width = newWidth;
      canvas.height = newHeight;

      // Restore background
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // Restore drawing if there was one
      if (imageData) {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0);
        };
        img.src = imageData;
      }
    }, [width, height, backgroundColor]);

    const getCoordinates = useCallback(
      (e: MouseEvent | TouchEvent): { x: number; y: number } => {
        const canvas = canvasRef.current;
        if (!canvas) return { x: 0, y: 0 };

        const rect = canvas.getBoundingClientRect();
        let clientX: number, clientY: number;

        if ('touches' in e) {
          clientX = e.touches[0]?.clientX || e.changedTouches[0]?.clientX || 0;
          clientY = e.touches[0]?.clientY || e.changedTouches[0]?.clientY || 0;
        } else {
          clientX = e.clientX;
          clientY = e.clientY;
        }

        // Scale coordinates to account for difference between display size and canvas resolution
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        return {
          x: (clientX - rect.left) * scaleX,
          y: (clientY - rect.top) * scaleY,
        };
      },
      []
    );

    // Mount / unmount: initialize canvas, attach global listeners
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      ctxRef.current = canvas.getContext('2d');
      resizeCanvas();

      // Initial clear (fill background)
      const ctx = ctxRef.current;
      if (ctx) {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }

      const handleGlobalMouseUp = () => {
        if (isDrawingRef.current) {
          isDrawingRef.current = false;
          if (hasSignatureRef.current) {
            onChangeRef.current?.(canvas.toDataURL());
          }
        }
      };

      let resizeTimer: ReturnType<typeof setTimeout> | null = null;
      const debouncedResize = () => {
        if (resizeTimer) clearTimeout(resizeTimer);
        resizeTimer = setTimeout(() => resizeCanvas(), 150);
      };
      window.addEventListener('resize', debouncedResize);
      window.addEventListener('mouseup', handleGlobalMouseUp);
      window.addEventListener('touchend', handleGlobalMouseUp);

      return () => {
        if (resizeTimer) clearTimeout(resizeTimer);
        window.removeEventListener('resize', debouncedResize);
        window.removeEventListener('mouseup', handleGlobalMouseUp);
        window.removeEventListener('touchend', handleGlobalMouseUp);
      };
    }, [resizeCanvas, backgroundColor]);

    const startDrawing = useCallback(
      (e: React.MouseEvent | React.TouchEvent) => {
        if (disabled) return;
        e.preventDefault();

        isDrawingRef.current = true;
        const coords = getCoordinates(e.nativeEvent);
        lastXRef.current = coords.x;
        lastYRef.current = coords.y;

        // Draw a dot for single clicks
        const ctx = ctxRef.current;
        if (ctx) {
          ctx.beginPath();
          ctx.arc(coords.x, coords.y, penWidth / 2, 0, Math.PI * 2);
          ctx.fillStyle = penColor;
          ctx.fill();
        }
      },
      [disabled, getCoordinates, penColor, penWidth]
    );

    const draw = useCallback(
      (e: React.MouseEvent | React.TouchEvent) => {
        if (!isDrawingRef.current || disabled) return;
        e.preventDefault();

        const coords = getCoordinates(e.nativeEvent);
        const ctx = ctxRef.current;

        if (ctx) {
          ctx.beginPath();
          ctx.moveTo(lastXRef.current, lastYRef.current);
          ctx.lineTo(coords.x, coords.y);
          ctx.strokeStyle = penColor;
          ctx.lineWidth = penWidth;
          ctx.lineCap = 'round';
          ctx.lineJoin = 'round';
          ctx.stroke();
        }

        lastXRef.current = coords.x;
        lastYRef.current = coords.y;
        setHasSignature(true);
      },
      [disabled, getCoordinates, penColor, penWidth]
    );

    const stopDrawing = useCallback(() => {
      if (isDrawingRef.current) {
        isDrawingRef.current = false;
        if (hasSignatureRef.current) {
          onChange?.(toDataURL());
        }
      }
    }, [onChange, toDataURL]);

    const handleMouseEnter = useCallback(
      (e: React.MouseEvent) => {
        // If mouse button is pressed when re-entering, resume drawing from new position
        if (e.buttons === 1 && !disabled) {
          const coords = getCoordinates(e.nativeEvent);
          isDrawingRef.current = true;
          lastXRef.current = coords.x;
          lastYRef.current = coords.y;
        }
      },
      [disabled, getCoordinates]
    );

    return (
      <div className={`signature-canvas-wrapper ${theme}`}>
        <canvas
          ref={canvasRef}
          className="signature-canvas"
          style={canvasStyle}
          onMouseDown={startDrawing}
          onMouseMove={draw}
          onMouseUp={stopDrawing}
          onMouseEnter={handleMouseEnter}
          onTouchStart={startDrawing}
          onTouchMove={draw}
          onTouchEnd={stopDrawing}
          onTouchCancel={stopDrawing}
        />
      </div>
    );
  }
));

(window as any).SignatureCanvasReact = SignatureCanvas;
